import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, RefreshCw, Phone, MessageCircle, Download, UserMinus,
  AlertTriangle, TrendingDown, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const RISK = {
  at_risk: { label: 'Под наблюдением', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  high_risk: { label: 'Высокий риск', cls: 'bg-red-100 text-red-800 border-red-300' },
  lost: { label: 'Потерян', cls: 'bg-gray-200 text-gray-700 border-gray-300' },
};

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n || 0) + ' ₸';

// Kazakhstan phone → wa.me digits (8XXXXXXXXXX → 7XXXXXXXXXX)
const waNumber = (phone) => {
  let d = (phone || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) d = '7' + d.slice(1);
  return d;
};

function AdminChurn({ user, onLogout }) {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState('all');
  const [risk, setRisk] = useState('all');

  useEffect(() => { fetchChurn(); /* eslint-disable-next-line */ }, [segment, risk]);

  const fetchChurn = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/churn?segment=${segment}&risk=${risk}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error('Не удалось загрузить аналитику оттока');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data?.clients?.length) return;
    const head = ['Клиент', 'Сегмент', 'Телефон', 'Заказов', 'Ритм (дн)', 'Молчит (дн)', 'Выручка', 'AOV', 'Риск', 'Последний заказ'];
    const rows = data.clients.map((c) => [
      c.name, c.segment, c.phone || '', c.orders, c.avg_gap_days, c.days_since,
      c.revenue, c.aov, RISK[c.risk]?.label || c.risk, c.last_order,
    ]);
    const csv = [head, ...rows].map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `winback_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/${currentLang}/admin`}>
              <Button variant="ghost" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Назад
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <UserMinus className="w-6 h-6 text-red-600" /> Отток клиентов · Win-back
              </h1>
              <p className="text-sm text-gray-500">Клиенты, у которых сбился привычный ритм заказов</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.clients?.length} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Экспорт CSV
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-6">
        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Card className="border-l-4 border-l-amber-400" data-testid="kpi-at-risk">
            <CardContent className="pt-4">
              <div className="text-xs text-gray-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Под наблюдением</div>
              <div className="text-3xl font-bold text-amber-600">{s.at_risk ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500" data-testid="kpi-high-risk">
            <CardContent className="pt-4">
              <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Высокий риск</div>
              <div className="text-3xl font-bold text-red-600">{s.high_risk ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-400" data-testid="kpi-lost">
            <CardContent className="pt-4">
              <div className="text-xs text-gray-500 flex items-center gap-1"><UserMinus className="w-3 h-3" /> Потеряны</div>
              <div className="text-3xl font-bold text-gray-600">{s.lost ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500" data-testid="kpi-revenue-at-risk">
            <CardContent className="pt-4">
              <div className="text-xs text-gray-500">Выручка под угрозой</div>
              <div className="text-2xl font-bold text-emerald-700">{fmt(s.revenue_at_risk)}</div>
              <div className="text-xs text-gray-400">LTV «наблюдение» + «высокий риск»</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={segment} onValueChange={setSegment}>
            <SelectTrigger className="w-[160px]" data-testid="segment-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все сегменты</SelectItem>
              <SelectItem value="B2B">B2B</SelectItem>
              <SelectItem value="B2C">B2C</SelectItem>
            </SelectContent>
          </Select>
          <Select value={risk} onValueChange={setRisk}>
            <SelectTrigger className="w-[180px]" data-testid="risk-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все уровни риска</SelectItem>
              <SelectItem value="at_risk">Под наблюдением</SelectItem>
              <SelectItem value="high_risk">Высокий риск</SelectItem>
              <SelectItem value="lost">Потеряны</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchChurn} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Обновить
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Список для возврата ({data?.total ?? 0})</CardTitle>
            <CardDescription>Отсортировано по выручке — звоните сначала самым ценным</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="churn-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-600">Клиент</th>
                    <th className="text-center px-3 py-3 text-xs font-medium text-gray-600">Сегмент</th>
                    <th className="text-center px-3 py-3 text-xs font-medium text-gray-600">Заказов</th>
                    <th className="text-center px-3 py-3 text-xs font-medium text-gray-600">Ритм</th>
                    <th className="text-center px-3 py-3 text-xs font-medium text-gray-600">Молчит</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-gray-600">Выручка (LTV)</th>
                    <th className="text-center px-3 py-3 text-xs font-medium text-gray-600">Риск</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-600">Контакт</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400"><RefreshCw className="w-6 h-6 mx-auto animate-spin" /></td></tr>
                  ) : !data?.clients?.length ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      Нет клиентов под риском оттока 🎉
                    </td></tr>
                  ) : data.clients.map((c) => {
                    const wa = waNumber(c.phone);
                    return (
                      <tr key={c.id} className="hover:bg-gray-50" data-testid={`churn-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 text-sm">{c.name}</div>
                          <div className="text-xs text-gray-400">{c.phone || 'без телефона'} · посл. {c.last_order}</div>
                        </td>
                        <td className="px-3 py-3 text-center"><Badge variant="outline" className="text-xs">{c.segment}</Badge></td>
                        <td className="px-3 py-3 text-center text-sm">{c.orders}</td>
                        <td className="px-3 py-3 text-center text-sm text-gray-600">~{c.avg_gap_days} дн</td>
                        <td className="px-3 py-3 text-center text-sm font-medium">{c.days_since} дн</td>
                        <td className="px-3 py-3 text-right text-sm font-semibold">{fmt(c.revenue)}</td>
                        <td className="px-3 py-3 text-center">
                          <Badge className={`text-xs border ${RISK[c.risk]?.cls}`}>{RISK[c.risk]?.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {c.phone && (
                              <a href={`tel:${c.phone}`} data-testid={`call-${c.id}`}>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Позвонить"><Phone className="w-4 h-4 text-emerald-600" /></Button>
                              </a>
                            )}
                            {wa && (
                              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid={`wa-${c.id}`}>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="WhatsApp"><MessageCircle className="w-4 h-4 text-green-600" /></Button>
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminChurn;
